import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "../baseQuery";
import {
  CreateStandaloneTask,
  CreateTaskHub,
  getTaskHubParams,
  PaginatedTaskHubResponse,
  PatchStandaloneTask,
  StandaloneTask,
  StandaloneTaskResponse,
  TaskHubItem,
  TaskStandAloneAlarm,
  UpdateStandaloneTask,
} from "./models";

export const taskHubApi = createApi({
  baseQuery,
  reducerPath: "taskHubApi",
  tagTypes: ["Tasks"],
  endpoints: (builder) => ({
    getTasks: builder.query<PaginatedTaskHubResponse, getTaskHubParams>({
      query: (params) => ({
        url: "/api/v1/tasks/",
        method: "GET",
        params,
      }),
      providesTags: ["Tasks"],
    }),

    getTaskById: builder.query<
      TaskHubItem,
      {
        id: number;
        expand?: string;
      }
    >({
      query: ({ id, expand }) => ({
        url: `/api/v1/tasks/${id}/`,
        method: "GET",
        params: {
          ...(expand ? { expand } : {}),
        },
      }),
      providesTags: (_result, _err, { id }) => [{ type: "Tasks", id }],
    }),

    createTask: builder.mutation<CreateTaskHub, any>({
      query: (body) => ({
        url: "/api/v1/tasks/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Tasks"],
    }),

    updateTask: builder.mutation<any, any>({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/tasks/${id}/`,
        method: "PUT",
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Tasks", id }],
    }),

    deleteTask: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/v1/tasks/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Tasks"],
    }),
    uploadImage: builder.mutation<void, { id: number; file: File }>({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: `/api/v1/tasks/${id}/icon/`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Tasks"],
    }),
    uploadFile: builder.mutation<void, { id: number; file: File }>({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: `/api/v1/tasks/${id}/file/`,
          method: "POST",
          body: formData,
        };
      },
    }),
    deleteTaskFile: builder.mutation<void, { taskId: number; fileId: number }>({
      query: ({ taskId, fileId }) => ({
        url: `/api/v1/tasks/${taskId}/file/${fileId}/`,
        method: "DELETE",
      }),
    }),

    // Stand Alone
    getTasksStandAlone: builder.query<StandaloneTaskResponse, any>({
      query: (params) => ({
        url: "/api/v1/tasks_standalone/",
        method: "GET",
        params,
      }),
      providesTags: ["Tasks"],
    }),

    createTaskStandAlone: builder.mutation<
      StandaloneTask,
      CreateStandaloneTask
    >({
      query: (body) => ({
        url: "/api/v1/tasks_standalone/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Tasks"],
    }),

    getTaskStandAloneById: builder.query<
      StandaloneTask,
      { id: number; params?: any }
    >({
      query: ({ id, params }) => ({
        url: `/api/v1/tasks_standalone/${id}/`,
        method: "GET",
        params,
      }),
      providesTags: ["Tasks"],
    }),

    updateTaskStandAlone: builder.mutation<
      StandaloneTask,
      UpdateStandaloneTask
    >({
      query: ({ id, ...body }) => ({
        url: `/api/v1/tasks_standalone/${id}/`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Tasks"],
    }),

    patchTaskStandAlone: builder.mutation<StandaloneTask, PatchStandaloneTask>({
      query: ({ id, ...body }) => ({
        url: `/api/v1/tasks_standalone/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_res, _err, { id }) => [{ type: "Tasks", id }],
    }),

    deleteTaskStandAlone: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/v1/tasks_standalone/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Tasks"],
    }),

    runStandalone: builder.query<any, number>({
      query: (id) => ({
        url: `/api/v1/tasks_standalone/${id}/run_standalone_query/`,
        method: "GET",
      }),
    }),

    getTasksAlarmStandAlone: builder.query<TaskStandAloneAlarm[], any>({
      query: (params) => ({
        url: "/api/v1/tasks_standalone_alarms/",
        method: "GET",
        params,
      }),
      providesTags: ["Tasks"],
    }),

    deleteTaskStandAloneAlarmTask: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/v1/tasks_standalone_alarms/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Tasks"],
    }),
  }),
});

export const {
  // Tasks
  useGetTasksQuery,
  useGetTaskByIdQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useUploadImageMutation,
  useUploadFileMutation,
  useDeleteTaskFileMutation,
  useCreateTaskStandAloneMutation,
  useDeleteTaskStandAloneMutation,
  useGetTaskStandAloneByIdQuery,
  useGetTasksStandAloneQuery,
  useUpdateTaskStandAloneMutation,
  useRunStandaloneQuery,
  usePatchTaskStandAloneMutation,
  useGetTasksAlarmStandAloneQuery,
  useDeleteTaskStandAloneAlarmTaskMutation,
} = taskHubApi;
